import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eyebrow } from "../components/ui.jsx";
import { requestDeletion } from "../lib/deletionApi.js";
import { getSiteKey, renderTurnstile } from "../lib/turnstile.js";

/**
 * Public deletion-request form at `/{lang}/account/delete`. Required by
 * Google Play's "outside the app" deletion option. Anonymous — the user
 * doesn't sign in here; their email + a confirmation link prove ownership.
 */
export default function AccountDelete() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const [sentTo, setSentTo] = useState(null);
  const captchaRef = useRef(null);
  const widgetIdRef = useRef(null);

  const siteKey = getSiteKey();

  // Mount the Turnstile widget on first render. When VITE_TURNSTILE_SITE_KEY
  // is unset (dev) we skip the widget entirely and let the form submit
  // without a token — the backend bypasses verification in that mode too.
  useEffect(() => {
    if (!siteKey || !captchaRef.current) return;
    let cancelled = false;
    renderTurnstile(captchaRef.current, {
      siteKey,
      onToken: (token) => { if (!cancelled) setTurnstileToken(token); },
      onExpired: () => { if (!cancelled) setTurnstileToken(null); }
    }).then((id) => { widgetIdRef.current = id; }).catch(() => {});
    return () => { cancelled = true; };
  }, [siteKey]);

  async function onSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setErrorKey(null);

    if (siteKey && !turnstileToken) {
      setErrorKey("captchaMissing");
      return;
    }

    setSubmitting(true);
    const { ok, status, data } = await requestDeletion({
      email: email.trim(),
      reason: reason.trim() || null,
      turnstileToken: turnstileToken ?? "",
      locale: i18n.language?.split("-")[0] === "ro" ? "ro" : "en"
    });
    setSubmitting(false);

    if (ok && status === 204) {
      setSentTo(email.trim());
      return;
    }

    if (status === 400 && data?.error === "invalid_email") setErrorKey("errors.invalidEmail");
    else if (status === 400 && data?.error === "captcha_failed") {
      setErrorKey("errors.captchaFailed");
      if (window.turnstile && widgetIdRef.current) window.turnstile.reset(widgetIdRef.current);
      setTurnstileToken(null);
    }
    else if (status === 429) setErrorKey("errors.rateLimited");
    else if (status === 500) setErrorKey("errors.mailFailed");
    else setErrorKey("errors.unknown");
  }

  if (sentTo) {
    return (
      <main>
        <section className="bg-wash">
          <div className="container" style={{ padding: "56px 24px 64px", maxWidth: 720 }}>
            <Eyebrow>{t("deleteAccount.eyebrow")}</Eyebrow>
            <h1 className="tt-h tt-h-xl" style={{ marginTop: 24 }}>
              {t("deleteAccount.successTitle")}
            </h1>
            <p style={{ marginTop: 24, fontSize: 17, lineHeight: 1.6, color: "var(--ink-2)" }}>
              {t("deleteAccount.successBody", { email: sentTo })}
            </p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      <section className="bg-wash">
        <div className="container" style={{ padding: "56px 24px 32px", maxWidth: 720 }}>
          <Eyebrow>{t("deleteAccount.eyebrow")}</Eyebrow>
          <h1 className="tt-h tt-h-xl" style={{ marginTop: 24 }}>{t("deleteAccount.title")}</h1>
          <p style={{ marginTop: 20, fontSize: 17, lineHeight: 1.6, color: "var(--ink-2)" }}>
            {t("deleteAccount.intro")}
          </p>
        </div>
      </section>

      <section style={{ padding: "32px 0 96px" }}>
        <div className="container" style={{ maxWidth: 560 }}>
          <form onSubmit={onSubmit} style={{ background: "var(--ink)", color: "var(--bg)", borderRadius: 8, padding: "40px 36px" }}>
            <Field
              label={t("deleteAccount.emailLabel")}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            <Field
              label={t("deleteAccount.reasonLabel")}
              hint={t("deleteAccount.reasonHint")}
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              multiline
            />

            {siteKey ? (
              <div ref={captchaRef} style={{ marginTop: 20, marginBottom: 8 }} />
            ) : null}

            {errorKey ? (
              <div role="alert" style={{ marginTop: 16, padding: "12px 16px", background: "rgba(185, 28, 28, .18)", border: "1px solid rgba(185, 28, 28, .55)", borderRadius: 6, color: "#fee", fontSize: 14, lineHeight: 1.5 }}>
                {t(`deleteAccount.${errorKey}`)}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="btn btn-lg"
              style={{ marginTop: 24, width: "100%", background: "var(--persimmon)", color: "var(--bg)", opacity: submitting ? 0.6 : 1 }}
            >
              {submitting ? t("deleteAccount.sending") : t("deleteAccount.submit")}
            </button>
          </form>

          <div style={{ marginTop: 32, padding: "20px 24px", background: "var(--bg-2)", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, color: "var(--ink-2)", lineHeight: 1.6 }}>
            <p style={{ margin: 0 }}>{t("deleteAccount.altInstructions")}</p>
            <p style={{ margin: "12px 0 0", fontSize: 12.5, color: "var(--ink-3, var(--ink-2))" }}>
              {t("deleteAccount.billingNotice")}
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}

function Field({ label, hint, value, onChange, type = "text", multiline = false, required = false, autoComplete }) {
  const Tag = multiline ? "textarea" : "input";
  return (
    <label style={{ display: "block", marginBottom: 22 }}>
      <span style={{ display: "block", fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", color: "var(--marigold)", marginBottom: 8, fontWeight: 600 }}>{label}</span>
      <Tag
        type={multiline ? undefined : type}
        value={value}
        onChange={onChange}
        rows={multiline ? 3 : undefined}
        required={required}
        autoComplete={autoComplete}
        style={{
          width: "100%", padding: "14px 0", fontSize: 16,
          background: "transparent", color: "var(--bg)",
          border: "none", borderBottom: "1px solid rgba(255,255,255,.25)",
          outline: "none", fontFamily: "Inter, sans-serif",
          resize: multiline ? "vertical" : undefined
        }}
      />
      {hint ? (
        <span style={{ display: "block", marginTop: 6, fontSize: 12, color: "rgba(255,255,255,.55)" }}>{hint}</span>
      ) : null}
    </label>
  );
}
